package com.evoapps.evoshift.domain.usecase.kiosk

import com.evoapps.evoshift.domain.model.KioskClockEvent
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

/**
 * A text value the tablet keeps between app starts (preferences, NSUserDefaults, localStorage):
 * the offline queue, the session of the paired tablet.
 */
interface KioskStore {
    fun load(): String?
    fun save(value: String?)
}

class InMemoryKioskStore(private var value: String? = null) : KioskStore {
    override fun load(): String? = value
    override fun save(value: String?) {
        this.value = value
    }
}

/**
 * Clock events the tablet could not send (offline), oldest first, kept on the device until they
 * are sent. Each keeps its device time and its eventId, so the server records the time it was
 * tapped and a retried sync applies it once. The PIN stays with the event until then (only the
 * server can check it) and is gone from the device once the event is sent or refused.
 */
class KioskClockQueue(private val store: KioskStore) {
    private val json = Json { ignoreUnknownKeys = true }
    private val serializer = ListSerializer(KioskClockEvent.serializer())
    private val mutex = Mutex()
    private val _events = MutableStateFlow(read())

    /** What waits to be sent, oldest first. */
    val events: StateFlow<List<KioskClockEvent>> = _events.asStateFlow()

    private fun read(): List<KioskClockEvent> =
        store.load()?.let { runCatching { json.decodeFromString(serializer, it) }.getOrNull() }.orEmpty()

    private fun write(events: List<KioskClockEvent>) {
        store.save(if (events.isEmpty()) null else json.encodeToString(serializer, events))
        _events.value = events
    }

    suspend fun add(event: KioskClockEvent) = mutex.withLock {
        if (_events.value.none { it.eventId == event.eventId }) write((_events.value + event).sortedBy { it.deviceTime })
    }

    suspend fun remove(eventId: String) = mutex.withLock {
        write(_events.value.filterNot { it.eventId == eventId })
    }

    /** Forgets everything (the tablet was unpaired or left kiosk mode). */
    suspend fun clear() = mutex.withLock { write(emptyList()) }
}
